import React from "react";
import { createRoot, type Root } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { AuthProvider } from "./lib/auth-context";
import { ConfirmProvider } from "./components/confirm-provider";
import AppRouter from "./router/AppRouter";
import { UpdatePrompt } from "./components/ui/update-prompt";
import i18n, { i18nReady } from "./i18n";
import "../styles/app.css";

import { SYMFONY_ENV } from "./lib/env";
import { setupPwa } from "./lib/pwa";
import { APP_COMMIT, APP_VERSION_FULL } from "./lib/version";
import { scrubBreadcrumb, scrubEvent } from "./lib/sentry-scrub";

setupPwa();

const rootElement = document.getElementById('root');
const sentryDsn = rootElement?.dataset.sentryDsn || '';
const sentryTracesSampleRate = Number(rootElement?.dataset.sentryTracesSampleRate || '0');

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: SYMFONY_ENV,
    // Which build the error came from — the first thing a triage needs.
    release: APP_VERSION_FULL,
    initialScope: { tags: { commit: APP_COMMIT || 'unknown' } },
    // Personal data stays out of the monitor (docs/gdpr-plan.md 3.7).
    sendDefaultPii: false,
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
    tracesSampleRate: Number.isFinite(sentryTracesSampleRate) ? sentryTracesSampleRate : 0,
  });
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={
        <div style={{ padding: 24, textAlign: 'center' }}>
          <p>{i18n.t("common:errors.unexpected")}</p>
          <button onClick={() => window.location.reload()}>{i18n.t("common:errors.reload")}</button>
        </div>
      }>
      <AuthProvider>
        <ConfirmProvider>
          <AppRouter />
          <UpdatePrompt />
        </ConfirmProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

// Mount the app. Keep one root per container: when Vite re-executes this
// module (HMR), calling createRoot again on the same node leaves two React
// roots fighting over #root ("removeChild ... not a child of this node").
// Wait for the visitor's locale first: a non-default language arrives as its own
// chunk, and mounting before it lands would render a frame of missing keys.
const container = rootElement as (HTMLElement & { __reactRoot?: Root }) | null;
if (container) {
  // If that chunk fails to load we still start, in the default language.
  i18nReady.catch(() => undefined).then(() => {
    const root = container.__reactRoot ?? (container.__reactRoot = createRoot(container));
    root.render(<App />);
  });
}

if (import.meta.hot) {
  // This entry can't be fast-refreshed; reload fully instead of half-applying.
  import.meta.hot.accept(() => window.location.reload());
}
